import type { Permission } from '@jmc/shared';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout.js';
import { EmptyState, LoadingBlock } from './components/ui.js';
import { useAuth } from './lib/auth.js';
import { useI18n } from './lib/i18n.js';
import { AuditPage, ReportsPage } from './pages/Reports.js';
import { ChangePasswordPage } from './pages/ChangePassword.js';
import { DashboardPage } from './pages/Dashboard.js';
import { FileDetailPage } from './pages/FileDetail.js';
import { FilePrintFormPage } from './pages/FilePrintForm.js';
import { FileFormPage } from './pages/FileForm.js';
import { ImportPage } from './pages/Import.js';
import { IssuedPage } from './pages/Issued.js';
import { LocationsPage } from './pages/Locations.js';
import { PickListPage } from './pages/PickList.js';
import { LoginPage } from './pages/Login.js';
import { NotFoundPage } from './pages/NotFound.js';
import { RoomsPage } from './pages/Rooms.js';
import { SearchPage } from './pages/Search.js';
import { UsersPage } from './pages/Users.js';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingBlock />;
  if (!user) return <Navigate to="/login" replace />;
  // A forced password change is a dead end by design: every other route is
  // refused by the server anyway, so routing there avoids a wall of 403s.
  if (user.mustChangePassword) return <Navigate to="/password" replace />;
  return <>{children}</>;
}

function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: React.ReactNode;
}) {
  const { can } = useAuth();
  const { t } = useI18n();
  if (!can(permission)) {
    return (
      <EmptyState
        title={t('common.error')}
        hint="Your role does not have access to this screen."
      />
    );
  }
  return <>{children}</>;
}

export function App() {
  const { user, isLoading } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/password"
        element={
          isLoading ? (
            <LoadingBlock />
          ) : user ? (
            <div className="px-4 py-10">
              <ChangePasswordPage />
            </div>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="search" element={<SearchPage />} />
        {/* `new` must precede `:id`, or the detail route swallows it. */}
        <Route
          path="files/new"
          element={
            <RequirePermission permission="file:create">
              <FileFormPage />
            </RequirePermission>
          }
        />
        <Route path="files/:id" element={<FileDetailPage />} />
        <Route path="files/:id/print" element={<FilePrintFormPage />} />
        <Route
          path="files/:id/edit"
          element={
            <RequirePermission permission="file:update">
              <FileFormPage />
            </RequirePermission>
          }
        />
        <Route path="rooms" element={<RoomsPage />} />
        <Route path="almirahs" element={<LocationsPage />} />
        <Route path="pick-list" element={<PickListPage />} />
        <Route path="issued" element={<IssuedPage />} />
        <Route
          path="import"
          element={
            <RequirePermission permission="file:import">
              <ImportPage />
            </RequirePermission>
          }
        />
        <Route
          path="users"
          element={
            <RequirePermission permission="user:read">
              <UsersPage />
            </RequirePermission>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePermission permission="report:read">
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="audit"
          element={
            <RequirePermission permission="audit:read">
              <AuditPage />
            </RequirePermission>
          }
        />
        {/* Inside the layout, so a mistyped URL still shows the navigation
            rather than stranding the user on a bare page. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
